const activeLocks = new Map();

let savedStyles = null;
let visibilityListenerAttached = false;

const canUseDom = () => (
  typeof window !== 'undefined' &&
  typeof document !== 'undefined' &&
  document.body &&
  document.documentElement
);

const hasFixedLock = () => Array.from(activeLocks.values()).some((lock) => lock.fixed);

// True while any drawer/overlay holds a body scroll lock. Used to ignore the
// spurious iOS `resize` that fires when the body flips to position:fixed —
// recomputing the viewport height during that window reflows the background.
export const isBodyScrollLocked = () => activeLocks.size > 0;

const applyLockState = () => {
  if (!canUseDom() || !savedStyles || activeLocks.size === 0) return;

  const body = document.body;
  const html = document.documentElement;
  const fixedLockActive = hasFixedLock();

  body.style.overflow = 'hidden';
  html.style.overflow = 'hidden';

  if (fixedLockActive) {
    body.style.position = 'fixed';
    body.style.top = `-${savedStyles.scrollY}px`;
    body.style.width = '100%';
    return;
  }

  body.style.position = savedStyles.bodyPosition;
  body.style.top = savedStyles.bodyTop;
  body.style.width = savedStyles.bodyWidth;
};

const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    applyLockState();
  }
};

const attachVisibilityListener = () => {
  if (visibilityListenerAttached || !canUseDom()) return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = true;
};

const detachVisibilityListener = () => {
  if (!visibilityListenerAttached || !canUseDom()) return;
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerAttached = false;
};

const restoreSavedStyles = () => {
  if (!canUseDom() || !savedStyles) return;

  const body = document.body;
  const html = document.documentElement;
  const shouldRestoreScroll = savedStyles.hadFixedLock;
  const scrollY = savedStyles.scrollY;

  body.style.overflow = savedStyles.bodyOverflow;
  body.style.position = savedStyles.bodyPosition;
  body.style.top = savedStyles.bodyTop;
  body.style.width = savedStyles.bodyWidth;
  html.style.overflow = savedStyles.htmlOverflow;

  savedStyles = null;
  detachVisibilityListener();

  if (shouldRestoreScroll) {
    window.scrollTo(0, scrollY);
  }
};

export const lockBodyScroll = ({ fixed = true } = {}) => {
  if (!canUseDom()) return () => {};

  const token = Symbol('body-scroll-lock');

  if (!savedStyles) {
    const body = document.body;
    const html = document.documentElement;

    savedStyles = {
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      htmlOverflow: html.style.overflow,
      scrollY: window.scrollY,
      hadFixedLock: false,
    };
  }

  activeLocks.set(token, { fixed });
  if (fixed) savedStyles.hadFixedLock = true;

  attachVisibilityListener();
  applyLockState();

  let unlocked = false;

  return () => {
    if (unlocked) return;
    unlocked = true;

    const removedLock = activeLocks.get(token);
    if (!removedLock) return;

    activeLocks.delete(token);

    if (activeLocks.size === 0) {
      restoreSavedStyles();
      return;
    }

    applyLockState();

    if (removedLock.fixed && !hasFixedLock() && savedStyles) {
      window.scrollTo(0, savedStyles.scrollY);
    }
  };
};